import { TextField } from "@mui/material";
import { ChangeEvent, PropsWithChildren, useEffect, useRef, useState, type JSX } from 'react';
import useDebounce from "../../../hooks/use-debounce/UseDebounce";
import { SideSheetFieldPropsBase } from "../side-sheet/SideSheet";
import NumberFormatCustom from "../../numeric-format-custom/NumericFormatCustom";

export type SideSheetInputProps = {
    value: string;
    debounceDelay?: number;
    numeric?: boolean;
    decimals?: number;
    onChange?: (value: string) => void;
    onClick?: () => void;
    error?: boolean;
    startAdornment?: JSX.Element
} & SideSheetFieldPropsBase;

export default function SideSheetInput({
    id,
    testId,
    label,
    ariaLabel,
    readOnly,
    helperText,
    error,
    sx,
    disabled,
    value,
    debounceDelay = 500,
    onChange,
    onClick,
    numeric,
    decimals,
    startAdornment
}: PropsWithChildren<SideSheetInputProps>): JSX.Element {
    const [text, setText] = useState(value ?? "");
    const debouncedText = useDebounce(text, debounceDelay);
    const textChangeRef = useRef(onChange);

    useEffect(() => {
        textChangeRef.current = onChange;
    }, [onChange]);

    // Using onValueChange directly requires it to be added
    // to the dependency array, which causes recursive renders.
    useEffect(() => {
        textChangeRef.current && textChangeRef.current(debouncedText);
    }, [debouncedText]);

    useEffect(() => {
        setText(value ?? "");
    }, [value]);

    return (
        <TextField
            variant="filled"
            id={id}
            data-testid={testId}
            aria-label={ariaLabel}
            label={label}
            helperText={helperText}
            error={error}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setText(event.target.value);
            }}
            value={text}
            sx={{
                marginTop: 0,
                ...sx
            }}
            disabled={disabled}
            onClick={() => onClick && onClick()}
            slotProps={{
                input: {
                    readOnly: readOnly,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    inputComponent: numeric ? NumberFormatCustom as any : undefined,
                    inputProps: numeric ? {
                    
                        decimals: decimals ?? 2,
                        pattern: encodeURIComponent(`^[-+]?\\d*\\.?\\d{0,${decimals ?? 2}}$`),
                        inputMode: 'decimal',
                        maxLength: 128,
                    } : undefined,
                    startAdornment: startAdornment

                },

                inputLabel: {
                    shrink: true,
                }
            }} />
    );
}